import { useCallback, useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { ContributionPoint } from '@img-arena/consoles-api-streaming'
import { useTheme } from '@mui/material/styles'
import { DataCard } from '@/components/card'
import { OpenDialogButton } from '@/components/ConsoleDialog/handlers'
import { AddContributionPointForm } from '@/components/forms/AddContributionPointsForm/AddContributionPointForm'
import { ADORNMENT } from '@/components/forms/AddContributionPointsForm/constants'
import { ActionDeleteItem, ActionFormItem, ActionsMenu } from '@/components/table/actions'
import { FilterableTable, TableBase } from '@/components/table/base'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { streamingRestApi } from '@/service/streamingApi'
import { useContributionPointsStore } from '@/stores/useContributionPointsStore'

import {
  CONTRIBUTION_POINTS_COLUMNS,
  CONTRIBUTION_POINTS_SUBTABLE_COLUMNS,
  getContributionPointsFilters
} from '../ContributionPoints/contributionPoints.table'

export const TABLE_TEST_ID = 'Contribution Points Table'
const DELETE_CONTRIBUTION_POINT_CONFIRMATION_MESSAGE =
  'Are you sure that you want to delete all contribution points for this tournament? This will remove its association with events provisioned with it.'

export const FOLLOW_PROPERTY_TOURNAMENT = {
  id: -1,
  name: 'Follow Property',
  startDate: '',
  endDate: ''
} as const

export type FormattedContributionPointData = Pick<ContributionPoint, 'property' | 'tournament'> & {
  url: ContributionPoint[]
}

const makeContributionPointUrl = (value: string) => {
  return `srt://${value}${ADORNMENT}:5000`
}

const makeUniqueId = (row: ContributionPoint | FormattedContributionPointData) => {
  return String(row.property.id + (row.tournament?.id || 0))
}

export const ContributionPointsView = () => {
  const contributionPointsEndpoints = streamingRestApi.endpoints.contributionPoints
  const theme = useTheme()
  const { headerRow: headerRowSx, contentRow: contentRowSx } = theme.palette.subtable

  const { data, isLoading, isFetching, fetchData, setFilters, resetState, filters } =
    useContributionPointsStore(
      useShallow((state) => ({
        data: state.data,
        selectedFilters: state.filters,
        isLoading: state.status === STORE_STATUS.loading,
        isFetching: state.status === STORE_STATUS.fetching,
        fetchData: state.fetchData,
        useContributionPointsStore,
        setFilters: state.setFilters,
        resetState: state.resetState,
        filters: state.filters
      }))
    )

  const groupedData = useMemo(() => {
    return data.reduce(
      (acc: FormattedContributionPointData[], contributionPoint: ContributionPoint) => {
        const { property, tournament } = contributionPoint
        const uniqueId = Number(makeUniqueId(contributionPoint))

        if (!acc[uniqueId]) {
          acc[uniqueId] = {
            property,
            tournament: tournament || FOLLOW_PROPERTY_TOURNAMENT,
            url: []
          }
        }

        acc[uniqueId].url.push({
          ...contributionPoint,
          name: makeContributionPointUrl(contributionPoint.name)
        })

        return acc
      },
      []
    )
  }, [data])

  const tableFilters = useMemo(() => getContributionPointsFilters(groupedData), [groupedData])

  const handleOnGroupDelete = async (row: FormattedContributionPointData) => {
    const tournamentId = row.tournament?.id as number

    if (tournamentId === FOLLOW_PROPERTY_TOURNAMENT.id) {
      await contributionPointsEndpoints.deleteContributionPointProperty(row.property.id)
    } else {
      await contributionPointsEndpoints.deleteContributionPointTournament(tournamentId)
    }

    await fetchData()
  }

  const handleOnUrlDelete = async (row: ContributionPoint) => {
    await contributionPointsEndpoints.deleteContributionPoint(row.id)
    await fetchData()
  }

  const freeTextUrlMatcher = useCallback(
    (item: ContributionPoint) => {
      const freeTextValue = filters.url?.[0]?.value?.toString()
      if (!freeTextValue) return true

      return item.name.toLowerCase().includes(freeTextValue.toLowerCase())
    },
    [filters.url]
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [fetchData])

  return (
    <>
      <TitleSetter title="Contribution Points" />
      <DataCard
        title="Contribution Points"
        headerPostfixActions={
          <OpenDialogButton label="Add Contribution Points">
            <AddContributionPointForm onSubmit={fetchData} />
          </OpenDialogButton>
        }
      >
        <FilterableTable
          data={groupedData}
          tableId={TABLE_TEST_ID}
          loading={isLoading}
          filters={tableFilters}
          fetching={isFetching}
          columns={CONTRIBUTION_POINTS_COLUMNS}
          rowIdProperty={makeUniqueId}
          onFiltersChange={setFilters}
          clientSideSortAndPagination
          clientSideFilters
          exclusiveRowDetails
          persistExpandedRows
          renderRowDetails={(row: FormattedContributionPointData) => (
            <TableBase
              dense
              headerRowSx={headerRowSx}
              contentRowSx={contentRowSx}
              data={row.url.filter(freeTextUrlMatcher)}
              columns={CONTRIBUTION_POINTS_SUBTABLE_COLUMNS}
              disablePagination
              disableColumnVisibility
              rowActions={
                <ActionsMenu>
                  <ActionDeleteItem
                    displaySeparately
                    onDelete={handleOnUrlDelete}
                    successMessage={(row) => `Contribution Point ${row.name} deleted successfully`}
                    errorMessage={(row) => `Failed to delete Contribution Point ${row.name}`}
                    confirmationMessage={(row) =>
                      `Are you sure you want to delete Contribution Point ${row.name}?`
                    }
                  />
                </ActionsMenu>
              }
            />
          )}
          rowActions={
            <ActionsMenu>
              <ActionFormItem
                divider="after"
                getForm={(row: FormattedContributionPointData) => (
                  <AddContributionPointForm defaultValues={row} onSubmit={fetchData} open />
                )}
              />
              <ActionDeleteItem
                onDelete={handleOnGroupDelete}
                confirmationMessage={DELETE_CONTRIBUTION_POINT_CONFIRMATION_MESSAGE}
              />
            </ActionsMenu>
          }
        />
      </DataCard>
    </>
  )
}
